/**
 * Zustand Route Store
 *
 * Manages route state for the "Route rijden" feature.
 * Handles route data, stops, and completion status.
 * Integrates with IndexedDB for offline persistence.
 */

import { create } from 'zustand';
import { telRouteStops } from '../lib/utils/stops';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  RoutesObject,
  GeplandeRitWithDetails,
  GeplandeRitStatus,
  AttemptedAction,
  SkipReason
} from '../lib/salesforce/types';

// ============================================================================
// Types
// ============================================================================

interface PendingSync {
  id: string;
  type: 'complete_stop' | 'skip_stop' | 'partial_pickup' | 'heropen_stop';
  ritId: string;
  payload: {
    status: GeplandeRitStatus;
    werkOrderStatus?: string;
    skipReason?: string;
    skipNotes?: string;
    attemptedActions?: AttemptedAction[];
    pickedUpItemIds?: string[];
    notes?: string;
    reason?: string;
    completedAt: string;
  };
  createdAt: string;
  retryCount: number;
}

interface RouteState {
  // State
  selectedDate: string;
  routes: RoutesObject[];
  currentRoute: RoutesObject | null;
  stops: GeplandeRitWithDetails[];
  isLoading: boolean;
  isSyncing: boolean;
  error: string | null;
  lastSynced: string | null;
  pendingSyncs: PendingSync[];

  // Computed helpers (not persisted, set via selectors)
  completedStopsCount: number;
  totalStopsCount: number;

  // Actions
  setSelectedDate: (date: string) => void;
  setRoutes: (routes: RoutesObject[]) => void;
  setCurrentRoute: (route: RoutesObject | null) => void;
  setStops: (stops: GeplandeRitWithDetails[]) => void;
  setLoading: (loading: boolean) => void;
  setSyncing: (syncing: boolean) => void;
  setError: (error: string | null) => void;
  setLastSynced: (date: string | null) => void;

  // Route operations
  completeStop: (ritId: string, type: 'Ophalen' | 'Terugbrengen') => void;
  skipStop: (ritId: string, reason: string, attemptedActions?: AttemptedAction[], notes?: string) => void;
  partialPickupStop: (ritId: string, pickedUpItemIds: string[], notes?: string, reason?: string) => void;
  heropenStop: (ritId: string) => void;

  // Sync operations
  addPendingSync: (sync: Omit<PendingSync, 'id' | 'createdAt' | 'retryCount'>) => void;
  removePendingSync: (id: string) => void;
  incrementSyncRetry: (id: string) => void;
  clearPendingSyncs: () => void;

  // Reset
  reset: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const getInitialSelectedDate = (): string => {
  const today = new Date();
  return today.toISOString().split('T')[0] as string;
};

const initialState: Omit<RouteState,
  'setSelectedDate' | 'setRoutes' | 'setCurrentRoute' | 'setStops' |
  'setLoading' | 'setSyncing' | 'setError' | 'setLastSynced' |
  'completeStop' | 'skipStop' | 'partialPickupStop' | 'heropenStop' | 'addPendingSync' | 'removePendingSync' |
  'incrementSyncRetry' | 'clearPendingSyncs' | 'reset'
> = {
  selectedDate: getInitialSelectedDate(),
  routes: [],
  currentRoute: null,
  stops: [],
  isLoading: false,
  isSyncing: false,
  error: null,
  lastSynced: null,
  pendingSyncs: [],
  completedStopsCount: 0,
  totalStopsCount: 0,
};

// ============================================================================
// Store
// ============================================================================

export const useRouteStore = create<RouteState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // ========================================================================
      // Basic Setters
      // ========================================================================

      setSelectedDate: (date: string) => {
        set({ selectedDate: date });
      },

      setRoutes: (routes: RoutesObject[]) => {
        set({ routes });
      },

      setCurrentRoute: (route: RoutesObject | null) => {
        set({ currentRoute: route });
      },

      setStops: (stops: GeplandeRitWithDetails[]) => {
        // Eén teldefinitie voor alle schermen (lib/utils/stops.ts): geannuleerd telt niet mee.
        const { total: totalStopsCount, completed: completedStopsCount } = telRouteStops(stops);
        set({
          stops,
          completedStopsCount,
          totalStopsCount,
        });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      setSyncing: (syncing: boolean) => {
        set({ isSyncing: syncing });
      },

      setError: (error: string | null) => {
        set({ error });
      },

      setLastSynced: (date: string | null) => {
        set({ lastSynced: date });
      },

      // ========================================================================
      // Route Operations (Optimistic Updates)
      // ========================================================================

      /**
       * Complete a stop with optimistic update
       * Updates local state immediately, then queues sync
       */
      completeStop: (ritId: string, type: 'Ophalen' | 'Terugbrengen') => {
        const { stops, addPendingSync } = get();
        const completedAt = new Date().toISOString();

        // Determine werk order status based on type
        const werkOrderStatus = type === 'Ophalen' ? 'Opgehaald' : 'Teruggebracht';

        // Optimistic update - update stop in local state
        const updatedStops = stops.map(stop => {
          if (stop.Id === ritId) {
            return {
              ...stop,
              Status__c: 'Verwerkt' as GeplandeRitStatus,
              // Note: We'd also update the nested werk order if present
            };
          }
          return stop;
        });

        const completedCount = updatedStops.filter(s => s.Status__c === 'Verwerkt').length;

        set({
          stops: updatedStops,
          completedStopsCount: completedCount,
        });

        // Queue sync operation
        addPendingSync({
          type: 'complete_stop',
          ritId,
          payload: {
            status: 'Verwerkt',
            werkOrderStatus,
            completedAt,
          },
        });

        // Trigger immediate sync if online (imported dynamically to avoid circular dependency)
        if (navigator.onLine) {
          import('../lib/sync').then(({ syncEngine }) => {
            syncEngine.processQueue().catch(err => {
              console.error('[RouteStore] Failed to trigger immediate sync:', err);
            });
          });
        }
      },

      /**
       * Skip a stop with reason and attempted actions
       */
      skipStop: (ritId: string, reason: string, attemptedActions?: AttemptedAction[], notes?: string) => {
        const { stops, addPendingSync } = get();
        const completedAt = new Date().toISOString();

        // For skip, mark as Verwerkt and set skip fields for UI display
        const updatedStops = stops.map(stop => {
          if (stop.Id === ritId) {
            return {
              ...stop,
              Status__c: 'Verwerkt' as GeplandeRitStatus,
              Overgeslagen__c: true,
              Reden_Overslaan__c: reason as SkipReason,
            };
          }
          return stop;
        });

        const completedCount = updatedStops.filter(s => s.Status__c === 'Verwerkt').length;

        set({
          stops: updatedStops,
          completedStopsCount: completedCount,
        });

        // Queue sync with skip reason and attempted actions
        addPendingSync({
          type: 'skip_stop',
          ritId,
          payload: {
            status: 'Verwerkt',
            skipReason: reason,
            skipNotes: notes,
            attemptedActions,
            completedAt,
          },
        });

        // Trigger immediate sync if online
        if (navigator.onLine) {
          import('../lib/sync').then(({ syncEngine }) => {
            syncEngine.processQueue().catch(err => {
              console.error('[RouteStore] Failed to trigger immediate sync:', err);
            });
          });
        }
      },

      /**
       * Heropenen — de chauffeur sloeg de stop over, de klant belde terug. De
       * stop gaat terug naar "te doen" zodat de normale afrondflow weer werkt.
       */
      heropenStop: (ritId: string) => {
        const { stops, addPendingSync } = get();
        const completedAt = new Date().toISOString();

        const updatedStops = stops.map(stop => {
          if (stop.Id === ritId) {
            return {
              ...stop,
              Status__c: 'Ingepland' as GeplandeRitStatus,
              Overgeslagen__c: false,
              Reden_Overslaan__c: undefined,
              Opmerking_Overslaan__c: undefined,
            };
          }
          return stop;
        });

        const completedCount = updatedStops.filter(s => s.Status__c === 'Verwerkt').length;

        set({
          stops: updatedStops,
          completedStopsCount: completedCount,
        });

        addPendingSync({
          type: 'heropen_stop',
          ritId,
          payload: {
            status: 'Ingepland',
            completedAt,
          },
        });

        if (navigator.onLine) {
          import('../lib/sync').then(({ syncEngine }) => {
            syncEngine.processQueue().catch(err => {
              console.error('[RouteStore] Failed to trigger immediate sync:', err);
            });
          });
        }
      },

      /**
       * Partial pickup ("deels opgehaald") — chauffeur nam sommige kleden mee,
       * de rest bleef staan. De stop is afgerond; de WO komt (server-side) in de
       * na-ophaal-staat zodat office een extra ophaalrit kan plannen.
       */
      partialPickupStop: (ritId: string, pickedUpItemIds: string[], notes?: string, reason?: string) => {
        const { stops, addPendingSync } = get();
        const completedAt = new Date().toISOString();

        // Optimistic update — stop afgerond
        const updatedStops = stops.map(stop => {
          if (stop.Id === ritId) {
            return {
              ...stop,
              Status__c: 'Verwerkt' as GeplandeRitStatus,
            };
          }
          return stop;
        });

        const completedCount = updatedStops.filter(s => s.Status__c === 'Verwerkt').length;

        set({
          stops: updatedStops,
          completedStopsCount: completedCount,
        });

        addPendingSync({
          type: 'partial_pickup',
          ritId,
          payload: {
            status: 'Verwerkt',
            pickedUpItemIds,
            notes,
            reason,
            completedAt,
          },
        });

        // Trigger immediate sync if online
        if (navigator.onLine) {
          import('../lib/sync').then(({ syncEngine }) => {
            syncEngine.processQueue().catch(err => {
              console.error('[RouteStore] Failed to trigger immediate sync:', err);
            });
          });
        }
      },

      // ========================================================================
      // Sync Queue Management
      // ========================================================================

      addPendingSync: (sync) => {
        const { pendingSyncs } = get();
        const newSync: PendingSync = {
          ...sync,
          id: `${sync.ritId}-${Date.now()}`,
          createdAt: new Date().toISOString(),
          retryCount: 0,
        };
        set({ pendingSyncs: [...pendingSyncs, newSync] });
      },

      removePendingSync: (id: string) => {
        const { pendingSyncs } = get();
        set({ pendingSyncs: pendingSyncs.filter(s => s.id !== id) });
      },

      incrementSyncRetry: (id: string) => {
        const { pendingSyncs } = get();
        set({
          pendingSyncs: pendingSyncs.map(s =>
            s.id === id ? { ...s, retryCount: s.retryCount + 1 } : s
          ),
        });
      },

      clearPendingSyncs: () => {
        set({ pendingSyncs: [] });
      },

      // ========================================================================
      // Reset
      // ========================================================================

      reset: () => {
        set(initialState);
      },
    }),
    {
      name: 'kleda-route-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Only persist these fields
        selectedDate: state.selectedDate,
        pendingSyncs: state.pendingSyncs,
        lastSynced: state.lastSynced,
        // Don't persist routes/stops - they come from IndexedDB
      }),
    }
  )
);

// ============================================================================
// Selectors (for derived state)
// ============================================================================

export const selectCompletionProgress = (state: RouteState) => {
  if (state.totalStopsCount === 0) return 0;
  return Math.round((state.completedStopsCount / state.totalStopsCount) * 100);
};

export const selectPendingStops = (state: RouteState) => {
  return state.stops.filter(s => s.Status__c === 'Ingepland');
};

export const selectCompletedStops = (state: RouteState) => {
  return state.stops.filter(s => s.Status__c === 'Verwerkt');
};

export const selectHasPendingSyncs = (state: RouteState) => {
  return state.pendingSyncs.length > 0;
};

export const selectNextStop = (state: RouteState) => {
  const pendingStops = state.stops.filter(s => s.Status__c === 'Ingepland');
  if (pendingStops.length === 0) return null;

  // Return the stop with the lowest sequence number
  return pendingStops.reduce((min, stop) =>
    (stop.Route_Sequence__c ?? 999) < (min.Route_Sequence__c ?? 999) ? stop : min
  );
};
